import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper, IconButton, Tooltip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress, Chip,
} from '@mui/material';
import { Refresh, Assessment, TrendingUp, AccountBalance, Schedule, People } from '@mui/icons-material';
import { projectService, ProjectsSummary } from '../../services/projectService';
import { formatCurrency } from '../../utils/currency';

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  PLANNING: 'info', IN_PROGRESS: 'primary', ON_HOLD: 'warning', COMPLETED: 'success', CANCELLED: 'error',
};

const ProjectReportsPage: React.FC = () => {
  const [summary, setSummary] = useState<ProjectsSummary | null>(null);
  const [utilization, setUtilization] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, utilizationData] = await Promise.all([
        projectService.getProjectsSummary(),
        projectService.getResourceUtilization(),
      ]);
      setSummary(summaryData);
      setUtilization(utilizationData);
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const totalBudget = Number(summary?.projects.total_budget) || 0;
  const totalCost = Number(summary?.projects.total_cost) || 0;
  const budgetUtilization = totalBudget > 0 ? (totalCost / totalBudget * 100) : 0;

  const totalTasks = Number(summary?.tasks.total_tasks) || 0;
  const completedTasks = Number(summary?.tasks.completed_tasks) || 0;
  const taskCompletionRate = totalTasks > 0 ? (completedTasks / totalTasks * 100) : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Project Reports</Typography>
          <Typography variant="body2" color="text.secondary">Overview of all project metrics and KPIs</Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={fetchData}><Refresh /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {summary && (
        <>
          {/* Project Status Overview */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment /> Project Status Overview
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary">{summary.projects.total_projects}</Typography>
                <Typography variant="caption" color="text.secondary">Total Projects</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="info.main">{summary.projects.planning}</Typography>
                <Typography variant="caption" color="text.secondary">Planning</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">{summary.projects.in_progress}</Typography>
                <Typography variant="caption" color="text.secondary">In Progress</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main">{summary.projects.on_hold}</Typography>
                <Typography variant="caption" color="text.secondary">On Hold</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">{summary.projects.completed}</Typography>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="error.main">{summary.projects.cancelled}</Typography>
                <Typography variant="caption" color="text.secondary">Cancelled</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          {/* Financial Summary */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance /> Financial Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card><CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                <Typography variant="h5" fontWeight="bold">{formatCurrency(summary.projects.total_budget)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card><CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">{formatCurrency(summary.projects.total_cost)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card><CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">Budget Utilization</Typography>
                <Typography variant="h5" fontWeight="bold" color={budgetUtilization > 100 ? 'error.main' : 'success.main'}>
                  {budgetUtilization.toFixed(1)}%
                </Typography>
                <LinearProgress variant="determinate" value={Math.min(budgetUtilization, 100)}
                  color={budgetUtilization > 100 ? 'error' : budgetUtilization > 80 ? 'warning' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mt: 1 }} />
              </CardContent></Card>
            </Grid>
          </Grid>

          {/* Task Summary */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp /> Task Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold">{summary.tasks.total_tasks}</Typography>
                <Typography variant="caption" color="text.secondary">Total Tasks</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">{summary.tasks.completed_tasks}</Typography>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="error.main">{summary.tasks.overdue_tasks}</Typography>
                <Typography variant="caption" color="text.secondary">Overdue</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">{taskCompletionRate.toFixed(0)}%</Typography>
                <Typography variant="caption" color="text.secondary">Completion Rate</Typography>
                <LinearProgress variant="determinate" value={taskCompletionRate}
                  color="primary" sx={{ height: 6, borderRadius: 3, mt: 1 }} />
              </CardContent></Card>
            </Grid>
          </Grid>

          {/* Time Summary */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule /> Time Summary
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold">{summary.time.total_hours}h</Typography>
                <Typography variant="caption" color="text.secondary">Total Hours Logged</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">{summary.time.billable_hours}h</Typography>
                <Typography variant="caption" color="text.secondary">Billable Hours</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Resource Utilization */}
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <People /> Resource Utilization
      </Typography>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Resource</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Allocation</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Hours Logged</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {utilization.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No resource data available</Typography>
                </TableCell></TableRow>
              ) : utilization.map((item, index) => (
                <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Typography fontWeight="medium">{item.resource_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.resource_type} size="small" color="default" />
                  </TableCell>
                  <TableCell>{item.project_name || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress variant="determinate" value={item.allocation_percent || 0}
                        sx={{ width: 60, height: 6, borderRadius: 3 }} />
                      <Typography variant="caption">{item.allocation_percent || 0}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{Number(item.total_hours || 0).toFixed(1)}h</TableCell>
                  <TableCell>
                    <Chip label={item.is_active ? 'Active' : 'Inactive'} size="small"
                      color={item.is_active ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ProjectReportsPage;
